import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';

import {forkJoin} from 'rxjs';
import {map} from 'rxjs/operators';

import {
    InArrayValidator,
    ArrayEveryValidator,
    ArrayEveryValidatorKey,
    NotNullArrayValuesValidator,
    NotNullArrayValuesValidatorKey
} from '@spa/validators';

import {
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    UserRelationTypesEnum,
    CompanyEmployeesService,
    IndividualSubordinatesService,
    SupervisionResponseInterface,
    PermissionModel,
    SupervisedUserModel,
    SupervisionInputModel,
    SupervisedUsersCollection,
} from '@spa/api';

import {Helper, CheckboxControl, Peer} from '../../../shared/classes';
import {SelectOptionInterface} from '../../../shared/components/select';
import {ColorCodesEnum, ComponentTypesEnum} from '../../../shared/enums/common';

import {SupervisedRightsFormErrorStatesEnum} from '../enums';

import {
    SupervisedRightsRequiredErrorLabel,
    SupervisedRightsFormItems,
    SupervisedRightsIdSelector,
    SupervisedRightsFormInterface,
    SupervisedRightsArraySelector,
    SupervisedRightsFormArraySelector,
    SupervisedRightsInvalidErrorLabel,
    SupervisedRightsNonSupervisedUserSelector
} from '../../../../assets/descriptors';

export class SupervisedRightsForm {
    private readonly companyEmployeesService: CompanyEmployeesService;
    private readonly individualSubordinatesService: IndividualSubordinatesService;

    public form: FormGroup;
    public rightsErrors: Array<string[]>;
    public controls: SupervisedRightsFormInterface[];
    public supervisedRightsArraySelector: string;
    public supervisedRightsFormArraySelector: string;

    constructor(companyEmployeesService: CompanyEmployeesService, individualSubordinatesService: IndividualSubordinatesService) {
        this.companyEmployeesService = companyEmployeesService;
        this.individualSubordinatesService = individualSubordinatesService;

        this.rightsErrors = [];
        this.supervisedRightsArraySelector = SupervisedRightsArraySelector;
        this.supervisedRightsFormArraySelector = SupervisedRightsFormArraySelector;
        this.controls = [];
    }

    private initSupervisedRightsFormGroup(
        permissions: PermissionModel[],
        nonSupervisedUsersPeers: Peer[],
        supervisedUserIndex: number = 0,
        supervisedUser: SupervisedUserModel = null
    ): FormGroup {
        this.controls[supervisedUserIndex] = SupervisedRightsFormItems();
        this.rightsErrors[supervisedUserIndex] = [];

        const supervisedRightsControls = this.controls[supervisedUserIndex];
        const rightsFormArray = new FormArray([]);

        if (!supervisedUser) {
            /**
             * Init company employee control with options and validator only when form is used for supervised rights assignment
             */
            const usersIds = nonSupervisedUsersPeers.map((peer: Peer) => peer.key);

            supervisedRightsControls.nonSupervisedUsers.setValidators([Validators.required, InArrayValidator(usersIds)]);

            supervisedRightsControls.nonSupervisedUsers.options
                .splice(0, supervisedRightsControls.nonSupervisedUsers.options.length);

            supervisedRightsControls.nonSupervisedUsers.options
                .push(...this.initNonSupervisedUsersControlOptions(nonSupervisedUsersPeers));
        } else {
            supervisedRightsControls.nonSupervisedUsers.value = supervisedUser.name;
            supervisedRightsControls.supervisionId.value = supervisedUser.userSupervisionId;
            supervisedRightsControls.supervisionId.extendValidators(Validators.required);
        }

        supervisedRightsControls.rights = this.initSupervisedRightsCheckboxes(
            permissions,
            !supervisedUser ? [] : supervisedUser.permissionIds,
            !!supervisedUser
        );

        const permissionIds = supervisedRightsControls.rights.map((control: CheckboxControl, controlIndex) => {
            rightsFormArray.insert(controlIndex, new FormControl(control.value));

            return control.value as number;
        }).concat([null]);

        const rightFormArrayValidators = !supervisedUser ? [NotNullArrayValuesValidator(), ArrayEveryValidator(permissionIds)] :
            [ArrayEveryValidator(permissionIds)];

        rightsFormArray.setValidators(rightFormArrayValidators);

        return this.createSupervisedRightsFormGroup(supervisedRightsControls, rightsFormArray);
    }

    private initNonSupervisedUsersControlOptions(clientUsersPeers: Peer[]): SelectOptionInterface[] {
        return clientUsersPeers.map((peer: Peer) => {
            return {
                label: peer.value,
                value: peer.key
            };
        });
    }

    private initSupervisedRightsCheckboxes(
        permissions: PermissionModel[],
        supervisedUserPermissionIds: number[],
        disabledCheckbox: boolean
    ): CheckboxControl[] {
        return permissions.filter((permission: PermissionModel) => {
            return Helper.enumsHelper().permissionsEnumHelper()
                .getPortalSupervisedPermissions().indexOf(permission.permissionString) >= 0;
        }).sort((current: PermissionModel, next: PermissionModel) => {
            const currentPosition = Helper.enumsHelper().permissionsEnumHelper()
                .getPortalSupervisedPermissionPosition(current.permissionString);

            const nextPosition = Helper.enumsHelper().permissionsEnumHelper()
                .getPortalSupervisedPermissionPosition(next.permissionString);

            return currentPosition.col - nextPosition.col;
        }).map((permission: PermissionModel, index: number) => {
            return new CheckboxControl({
                name: index + '',
                value: permission.id,
                placeholder: Helper.textsHelper().getPortalSupervisedPermissionName(permission.permissionString),
                validators: [],
                componentType: ComponentTypesEnum.Checkbox,
                checked: supervisedUserPermissionIds.indexOf(permission.id) >= 0,
                disabled: disabledCheckbox,
                color: ColorCodesEnum.Primary,
                expectBooleanValue: false
            });
        });
    }

    private createSupervisedRightsFormGroup(
        controls: SupervisedRightsFormInterface,
        rightsFormArray: FormArray
    ): FormGroup {
        return new FormGroup({
                [SupervisedRightsIdSelector]: new FormControl(
                    controls.supervisionId.value,
                    controls.supervisionId.validators
                ),
                [SupervisedRightsNonSupervisedUserSelector]: new FormControl(
                    controls.nonSupervisedUsers.value,
                    controls.nonSupervisedUsers.validators
                ),
                [SupervisedRightsArraySelector]: rightsFormArray
            }
        );
    }

    private getClientUsersService(clientUserRelationType: number): CompanyEmployeesService | IndividualSubordinatesService {
        switch (clientUserRelationType) {
            case UserRelationTypesEnum.CompanyEmployee:
                return this.companyEmployeesService;
            case UserRelationTypesEnum.IndividualSubordinate:
                return this.individualSubordinatesService;
            default:
                return null;

        }
    }

    private createAssignmentRequest(
        clientUserId: number,
        clientUsersService: CompanyEmployeesService | IndividualSubordinatesService,
        nonSupervisedUserId: number,
        supervisedPermissionIds: number[]
    ): ServerResponse<boolean> {
        const model = new SupervisionInputModel(clientUserId, nonSupervisedUserId, supervisedPermissionIds);

        return clientUsersService.assignParentSupervision(model).pipe(
            map((assignmentResponse: ServerResponseData<SupervisionResponseInterface>) => {
                if (assignmentResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<SupervisionResponseInterface>(
                        assignmentResponse,
                        SupervisedRightsFormErrorStatesEnum.AssignRightsFailed
                    );
                }

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    private createUpdateRequest(
        clientUsersService: CompanyEmployeesService | IndividualSubordinatesService,
        supervisedUser: SupervisedUserModel,
        supervisedPermissionIds: number[]
    ): ServerResponse<boolean> {
        if (Helper.compareSimpleArrays<number>(supervisedUser.permissionIds, supervisedPermissionIds)) {
            return Helper.createSuccessResponseObservable(true);
        }

        const currentPermissionIds = [...supervisedUser.permissionIds];

        supervisedUser.permissionIds = supervisedPermissionIds;

        return clientUsersService.updateSupervision(supervisedUser).pipe(
            map((updateResponse: ServerResponseData<null>) => {
                if (updateResponse.status !== HttpStatusCodesEnum.Ok) {
                    supervisedUser.permissionIds = currentPermissionIds;

                    return Helper.errorsHelper().createErrorResponse<null>(
                        updateResponse,
                        SupervisedRightsFormErrorStatesEnum.UpdateRightsFailed,
                        supervisedUser.name
                    );
                }

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    private createUnAssignRequest(
        clientUsersService: CompanyEmployeesService | IndividualSubordinatesService,
        supervisedUser: SupervisedUserModel,
        supervisedUsers: SupervisedUsersCollection
    ): ServerResponse<boolean> {
        return clientUsersService.unAssignParentSupervision(supervisedUser.userSupervisionId).pipe(
            map((unAssignResponse: ServerResponseData<null>) => {
                if (unAssignResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse(
                        unAssignResponse,
                        SupervisedRightsFormErrorStatesEnum.DeleteRightsFailed,
                        supervisedUser.name
                    );
                }

                supervisedUsers.removeSupervisedUserBySupervisionId(supervisedUser.userSupervisionId);

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    toggleControls(disabled: boolean): void {
        this.controls.forEach((controls: SupervisedRightsFormInterface) => {
            controls.rights.forEach((rightControl: CheckboxControl) => rightControl.disabled = disabled);
        });
    }

    initForm(
        permissions: PermissionModel[],
        nonSupervisedUsersPeers: Peer[] = [],
        supervisedUsers: SupervisedUsersCollection = null
    ): void {
        this.form = new FormGroup({});
        this.controls.splice(0, this.controls.length);

        const supervisedRightsArray = new FormArray([]);

        /**
         * Init form for supervised rights assignment
         */
        if (!supervisedUsers) {
            const supervisedRightsFormGroup = this.initSupervisedRightsFormGroup(permissions, nonSupervisedUsersPeers);

            supervisedRightsArray.insert(0, supervisedRightsFormGroup);
        } else {
            supervisedUsers.supervisedUsers.forEach((supervisedUser: SupervisedUserModel, supervisedUserIndex: number) => {
                const assignRightsFormGroup = this.initSupervisedRightsFormGroup(
                    permissions,
                    nonSupervisedUsersPeers,
                    supervisedUserIndex,
                    supervisedUser
                );

                supervisedRightsArray.insert(supervisedUserIndex, assignRightsFormGroup);
            });
        }

        this.form.addControl(SupervisedRightsFormArraySelector, supervisedRightsArray);
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        if (this.form.invalid) {
            const supervisedRightsFormArray = this.form.get(SupervisedRightsFormArraySelector);

            this.controls.forEach((controls: SupervisedRightsFormInterface, controlsIndex: number) => {
                const supervisedRightsFormGroup = supervisedRightsFormArray.get(controlsIndex + '') as FormGroup;

                this.rightsErrors[controlsIndex].splice(0, this.rightsErrors[controlsIndex].length);

                if (supervisedRightsFormGroup) {
                    Object.keys(supervisedRightsFormGroup.controls).forEach((formControlKey: string) => {
                        const formControl = supervisedRightsFormGroup.get(formControlKey);

                        if (formControl && formControl.invalid) {
                            const filteredControls = Object.keys(controls)
                                .filter((controlKey: string) => formControlKey === controls[controlKey].name)
                                .map((controlKey: string) => controls[controlKey]);

                            const errors = Object.keys(formControl.errors).map((errorKey: string) => {
                                let placeholder = '';

                                if (formControlKey !== SupervisedRightsArraySelector) {
                                    placeholder = (filteredControls.length !== 0) ? filteredControls[0].placeholder : '';
                                } else {
                                    switch (errorKey) {
                                        case NotNullArrayValuesValidatorKey:
                                            placeholder = SupervisedRightsRequiredErrorLabel;
                                            break;
                                        case ArrayEveryValidatorKey:
                                            placeholder = SupervisedRightsInvalidErrorLabel;
                                            break;
                                    }
                                }

                                return messageFunction(placeholder)[errorKey];
                            });

                            if (formControlKey !== SupervisedRightsArraySelector) {
                                if (filteredControls.length !== 0) {
                                    filteredControls[0].setErrors(errors);
                                }
                            } else {
                                this.rightsErrors[controlsIndex].push(...errors);
                            }
                        }
                    });
                }
            });

            return false;
        }

        return true;
    }

    createRequest(
        clientUserId: number,
        clientUserRelationType: number,
        supervisedUsers?: SupervisedUsersCollection
    ): ServerResponse<boolean> {
        const supervisedRightsFormArray = this.form.get(SupervisedRightsFormArraySelector) as FormArray;
        const clientUsersService = this.getClientUsersService(clientUserRelationType);

        if (!clientUsersService) {
            return Helper.errorsHelper().createBadRequestResponseObservable();
        }

        const requests = supervisedRightsFormArray.controls.map((supervisedRightsFormGroup: FormGroup) => {
            const supervisedId = supervisedRightsFormGroup.value[SupervisedRightsIdSelector];

            const supervisedPermissionIds = supervisedRightsFormGroup.get(SupervisedRightsArraySelector).value
                .filter((id: number) => (id !== null));

            if (!supervisedId) {
                const nonSupervisedUserId = supervisedRightsFormGroup.value[SupervisedRightsNonSupervisedUserSelector];

                return this.createAssignmentRequest(clientUserId, clientUsersService, nonSupervisedUserId, supervisedPermissionIds);
            }

            const supervisedUser = supervisedUsers.getSupervisedUserBySupervisionId(supervisedId);

            if (!supervisedUser) {
                return Helper.errorsHelper().createBadRequestResponseObservable();
            }

            if (supervisedPermissionIds.length !== 0) {
                return this.createUpdateRequest(clientUsersService, supervisedUser, supervisedPermissionIds);
            }

            return this.createUnAssignRequest(clientUsersService, supervisedUser, supervisedUsers);
        });

        if (requests.length !== 0) {
            return forkJoin(requests).pipe(
                map((responses: ServerResponseData<boolean>[]) => {
                    if (Helper.errorsHelper().checkForErrorResponses<boolean>(responses)) {
                        return Helper.errorsHelper().createReducedErrorResponse<boolean>(responses);
                    }

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }
}
